import React, { useEffect, useMemo, useRef, useState } from "react"
import { Helmet } from "react-helmet"

import { useAnalytics } from "@hooks/useAnalytics"
import { useCore, useStorage } from "@hooks/useCore"
import { useMedia } from "@hooks/useMedia"
import { useHydration } from "@hooks/useHydration"
import { useResults, useSearch } from "@usereactify/search"
import useCollectionSchema from "@app/hooks/useCollectionSchema"

import ProductGridEmpty from "./ProductGridEmpty"
import ProductGridError from "./ProductGridError"
import ProductGridContainer from "./ProductGridContainer"
import ProductGridCallout from "./ProductGridCallout"
import ProductGridItem from "./ProductGridItem"

import ProductCardLoader from "@components/Product/ProductCardLoader"
import ProductCardDrawer from "@components/ProductCardDrawer"

import { Pagination } from "@components/Search/Pagination/Pagination"

import FilterDrawer from "@app/components/Filter/FilterDrawer"
import FilterList from "@app/components/Filter/FilterList"
import FilterSlider from "@app/components/Filter/FilterSlider"
import { FilterRange } from "@app/components/Filter/FilterRange"

import FiltersSelectedComponent from "@app/components/Filter/FiltersSelectedComponent"
import SortByDropdown from "@app/components/Filter/SortByDropdown"
import PerPage from "@app/components/Filter/PerPage"

import { colors } from "@app/theme/colors"

import { Filters, FiltersSelected, Results, ResultStateProvider, SortBy } from "@usereactify/search"
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from "body-scroll-lock"

import { Box, Button, Container, Flex, Text } from "@chakra-ui/react"

import type { CollectionProps } from "@root/types/global"

interface ProductGridProps extends GatsbyTypes.SanitySectionProductGrid {
  collection?: CollectionProps | GatsbyTypes.SanityCollection
  setQuantity: number
  setGrProductData: any
}

const ProductGrid = ({
  resultsPerPage,
  resultsPerRowMobile,
  resultsPerRowDesktop,
  theme,
  collection,
  setQuantity,
  setGrProductData,
}: ProductGridProps) => {
  const drawerRef = useRef<any>(null)
  const [drawerOpen, triggerDrawerOpen] = useState(false)
  const [loadedProductsCount, setLoadedProductsCount] = useState(0)
  const [quickViewProduct, setQuickViewProduct] = useState<BundleProduct | null>(null)

  const {
    helpers: { getUrlParameter, setUrlParameter },
  } = useCore()

  const { getStorage } = useStorage()
  const { isHydrated } = useHydration()
  const { isBase, isMedium } = useMedia()

  const { trackProductListView } = useAnalytics()
  const { results } = useResults()
  const { searchTerm } = useSearch()

  useEffect(() => {
    if (results?.length !== loadedProductsCount) {
      trackProductListView(
        results.slice(loadedProductsCount),
        collection?.shopify?.handle ? collection?.shopify?.handle : "Search",
        collection?.shopify?.handle === "search" && searchTerm ? searchTerm : null
      )
      setLoadedProductsCount(results?.length)
    }
  }, [collection?.shopify?.handle, trackProductListView, loadedProductsCount, results, searchTerm, collection])

  const pageSizeQueryParam = useMemo(() => getUrlParameter("pageSize"), [getUrlParameter])
  const [perPage, setPerPage] = useState(pageSizeQueryParam || resultsPerPage || 24)

  const setPageSize = (pageSize: number) => {
    setPerPage(pageSize)
    window.history.replaceState(null, window.document.title, setUrlParameter("pageSize", pageSize))
  }

  const setDrawerOpen = (isOpen: boolean) => {
    const ele = document.getElementById("purebaby-header")
    if (isOpen) {
      disableBodyScroll(drawerRef?.current)
      if (ele) ele.style.opacity = "0"
    } else {
      if (ele) ele.style.opacity = "1"
      enableBodyScroll(drawerRef?.current)
    }
    triggerDrawerOpen(isOpen)
  }

  const filterTypeToRender = useMemo(() => {
    if (isHydrated) {
      if (isMedium) return "list"
      return "drawer"
    }
    return
  }, [isHydrated, isMedium])

  useEffect(() => {
    clearAllBodyScrollLocks()
  }, [])

  useEffect(() => {
    const lastScrollString = getStorage("collection_scroll_position")
    const collectionHandle = `/collections/${collection?.shopify?.handle}`
    if (lastScrollString && collection?.shopify?.handle) {
      try {
        const lastScroll = JSON.parse(lastScrollString)
        const historyString = window.localStorage.getItem("reachGlobalHistory")
        const history = JSON.parse(historyString || "[]")

        if (
          lastScroll?.collectionHandle &&
          lastScroll?.collectionHandle === collectionHandle &&
          (history?.[history.length - 2]?.startsWith("/products") || history?.[history.length - 1]?.startsWith("/products"))
        ) {
          setTimeout(() => {
            window.scrollTo(0, lastScroll?.position)
          }, 1000)
        }
        // setStorage("collection_scroll_position", null)
      } catch (error) {
        // setStorage("collection_scroll_position", null)
        throw new Error("last scroll position object parse error")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const collectionPage = !!collection

  const renderResultCardProduct = React.useCallback(
    props => {
      const product = props.product
      return (
        <ProductGridItem
          itemRef={props.itemRef}
          collection={collection}
          key={product.id}
          product={product}
          pagePosition={props.pagePosition}
          onQuickView={setQuickViewProduct}
          handleClick={props.handleClick}
        />
      )
    },
    [collection]
  )

  const collectionSchema = useCollectionSchema(collection, results)

  if (quickViewProduct) !!setGrProductData ? setGrProductData(quickViewProduct) : null
  return (
    <>
      <Helmet>
        <script type="application/ld+json">{collectionSchema}</script>
      </Helmet>

      <Box position="relative" bg={theme === "Dark" ? "brand.beige.dark" : "white"}>
        <div
          id="reactify-search-product-grid-anchor"
          style={{
            position: "absolute",
            top: "-59px",
            left: 0,
          }}
        ></div>
        <Container py={4} pl={collectionPage ? 0 : [2, 4.75]} pr={collectionPage ? 0 : [2, 4.75]} pt={3}>
          {filterTypeToRender && (
            <Flex
              mb={4}
              flexDirection={"column"}
              position={`sticky`}
              top={{ base: `48px`, md: `56px` }}
              paddingTop={{ md: `10px` }}
              zIndex={9}
              backgroundColor={`white`}
              marginBottom={{ base: 3, md: 4 }}
            >
              {filterTypeToRender == "drawer" ? (
                <Box overflow={`hidden`}>
                  <Button variant="outline" onClick={() => setDrawerOpen(!drawerOpen)} width={`full`}>
                    FILTERS & SORT
                  </Button>
                  <FilterDrawer ref={drawerRef} isOpen={drawerOpen} onClose={setDrawerOpen} setPerPage={setPerPage} perPage={perPage} />
                </Box>
              ) : (
                <>
                  <Flex>
                    <Filters renderFilterList={FilterList} renderFilterRange={FilterRange} renderFilterSlider={FilterSlider} />
                    <SortBy
                      render={props => {
                        return <SortByDropdown {...props} />
                      }}
                    ></SortBy>
                    <PerPage setPerPage={setPageSize} perPage={perPage} />
                    <ResultStateProvider
                      render={state => {
                        return (
                          <Text ml="auto" size="md" color={colors.text.secondary}>
                            {state?.hits?.total || 0} {collection?.shopify?.handle == "shop-the-look" ? "look" : "product"}
                            {state?.hits?.total === 1 ? "" : "s"}
                          </Text>
                        )
                      }}
                    ></ResultStateProvider>
                  </Flex>
                  <Flex mt={1}>
                    <FiltersSelected render={props => <FiltersSelectedComponent {...props} />}></FiltersSelected>
                  </Flex>
                </>
              )}
            </Flex>
          )}
          <Results
            pageSize={perPage}
            renderNoResults={() => <ProductGridEmpty />}
            renderError={props => <ProductGridError {...props} />}
            renderLoading={() => (
              <ProductGridContainer>
                {[...Array(perPage)].map((_, i) => (
                  <ProductCardLoader key={i} />
                ))}
              </ProductGridContainer>
            )}
            listStyle={{
              display: "grid",
              gap: "12px",
              gridTemplateColumns: `repeat(${
                isBase ? resultsPerRowMobile || 1 : collection?.shopify?.handle == "shop-the-look" ? 3 : resultsPerRowDesktop || 4
              }, minmax(0, 1fr))`,
            }}
            renderResultCardProduct={renderResultCardProduct}
            renderResultCardCallout={props => <ProductGridCallout {...props} />}
            renderPaginationNumbered={props => {
              return <Pagination pageSize={perPage} {...props} />
            }}
          />
        </Container>
        {quickViewProduct && (
          <ProductCardDrawer
            product={quickViewProduct}
            location={location}
            updateQuickViewProduct={product => setQuickViewProduct(product)}
            onClose={() => setQuickViewProduct(null)}
            setQuantity={setQuantity}
          />
        )}
      </Box>
    </>
  )
}

export default React.memo(ProductGrid)
